import { inject, Injectable } from '@angular/core';
import { FormDetails, FormResponseDetails } from '@shared/models/form/form.model';
import { OfflineFormContextResponse } from '@shared/models/offline/offline-submission.model';
import { ServiceRequestFormPlacementDetail } from '@shared/models/service-request/service-request.model';
import {
  mapOfflineContextToPlacementDetail,
} from '@features/service/pages/service-request-form-page/service-request-form-placement.util';
import { SERVICE_REQUEST_FORM_SUBMISSION_STATUS } from '@features/service/constants/service-request-form-submission-status.constants';
import { OfflineSyncStatus } from '@shared/constants/offline.constants';
import { TOAST_TYPE } from '@shared/constants/toast.constants';
import { OfflineFormLocalState } from '@shared/models/offline/offline.model';
import { OfflineQueueService } from '@shared/services/offline/offline-queue.service';
import { OfflineStorageService } from '@shared/services/offline/offline-storage.service';
import { OfflineUiService } from '@shared/services/offline/offline-ui.service';
import { ToastService } from '@shared/services/toast/toast.service';
import {
  buildFormLocalStateKey,
  buildOfflineCacheKey,
  generateClientMutationId,
  generateTempResponseId,
  normalizeFormCode,
} from '@shared/utilities/offline-cache-key.util';
import { buildOptimisticOfflineResponseJson } from '@shared/utilities/offline-form-response-json.util';

const UNSYNCED_STATUSES: OfflineSyncStatus[] = ['saved-locally', 'syncing', 'sync-failed'];

const LOCAL_FORM_OVERLAY_STATUSES: OfflineSyncStatus[] = [...UNSYNCED_STATUSES, 'draft'];

export type CommitLocalSaveParams = {
  clientId: string;
  serviceRequestId: number;
  formCode: string;
  formId: number;
  formPlacementId: number;
  mode: 'create' | 'edit';
  formSnapshot: FormDetails | FormResponseDetails;
  capturedAt?: string;
  pendingComplete?: boolean;
};

export function isPendingLocalComplete(state: OfflineFormLocalState | undefined): boolean {
  return state?.pendingComplete === true;
}

type PendingLocalCommit = {
  timer: ReturnType<typeof setTimeout>;
  params: CommitLocalSaveParams;
  onCommitted?: () => void;
};

@Injectable({ providedIn: 'root' })
export class OfflineFormStateService {
  private localCommitTimers = new Map<string, PendingLocalCommit>();
  private readonly toastService = inject(ToastService);

  constructor(
    private readonly storage: OfflineStorageService,
    private readonly queue: OfflineQueueService,
    private readonly ui: OfflineUiService,
  ) {}

  async hasUnsyncedLocal(clientId: string, serviceRequestId: number, formCode: string): Promise<boolean> {
    const state = await this.loadFormStateRecord(clientId, serviceRequestId, formCode);
    return !!state && UNSYNCED_STATUSES.includes(state.syncStatus);
  }

  async hasUnsyncedLocalForRequest(
    clientId: string,
    serviceRequestId: number,
    params: { formCode?: unknown; formId?: unknown; formPlacementId?: unknown },
  ): Promise<boolean> {
    const formCode = String(params.formCode ?? '').trim();
    if (formCode) {
      return this.hasUnsyncedLocal(clientId, serviceRequestId, formCode);
    }

    const formPlacementId = Number(params.formPlacementId ?? 0);
    const formId = Number(params.formId ?? 0);
    if (!formPlacementId && !formId) {
      return false;
    }

    const states = await this.storage.listFormLocalStates();
    return states.some((state) =>
      state.clientId === clientId
      && state.serviceRequestId === serviceRequestId
      && (
        (formPlacementId > 0 && state.formPlacementId === formPlacementId)
        || (formId > 0 && state.formId === formId)
      )
      && UNSYNCED_STATUSES.includes(state.syncStatus),
    );
  }

  async refreshFormSyncStatus(clientId: string, serviceRequestId: number, formCode: string): Promise<void> {
    const state = await this.loadFormStateRecord(clientId, serviceRequestId, formCode);
    if (!state || !UNSYNCED_STATUSES.includes(state.syncStatus)) {
      this.ui.setFormSyncStatus(null);
      return;
    }

    this.ui.setFormSyncStatus(state.syncStatus);
  }

  async loadFormState(
    clientId: string,
    serviceRequestId: number,
    formCode: string,
  ): Promise<FormDetails | FormResponseDetails | undefined> {
    const state = await this.loadFormStateRecord(clientId, serviceRequestId, formCode);
    if (state && LOCAL_FORM_OVERLAY_STATUSES.includes(state.syncStatus)) {
      const uiStatus = state.syncStatus === 'draft' ? 'saved-locally' : state.syncStatus;
      this.ui.setFormSyncStatus(uiStatus);
      return structuredClone(state.formSnapshot);
    }

    if (state) {
      this.ui.setFormSyncStatus(null);
      return structuredClone(state.formSnapshot);
    }

    this.ui.setFormSyncStatus(null);
    return undefined;
  }

  async seedDownloadedForm(
    clientId: string,
    serviceRequestId: number,
    formCode: string,
    formId: number,
    formSnapshot: FormDetails | FormResponseDetails,
    mode: 'create' | 'edit',
  ): Promise<void> {
    const stateKey = buildFormLocalStateKey(clientId, serviceRequestId, formCode);
    const existing = await this.storage.getFormLocalState(stateKey);
    if (existing && UNSYNCED_STATUSES.includes(existing.syncStatus)) {
      return;
    }

    await this.persistFormLocalState({
      stateKey,
      clientId,
      serviceRequestId,
      formCode: normalizeFormCode(formCode),
      formId,
      mode,
      responseId: 'responseId' in formSnapshot ? formSnapshot.responseId : undefined,
      clientMutationId: existing?.clientMutationId ?? generateClientMutationId(),
      formSnapshot: structuredClone(formSnapshot),
      lastModifiedAt: new Date().toISOString(),
      syncStatus: 'synced',
      lastSyncedAt: new Date().toISOString(),
    });
  }

  hasPendingLocalCommit(stateKey: string): boolean {
    return this.localCommitTimers.has(stateKey);
  }

  async getPendingComplete(
    clientId: string,
    serviceRequestId: number,
    formCode: string,
  ): Promise<boolean> {
    const state = await this.loadFormStateRecord(clientId, serviceRequestId, formCode);
    return isPendingLocalComplete(state);
  }

  scheduleLocalCommit(params: CommitLocalSaveParams, onCommitted?: () => void): void {
    const stateKey = buildFormLocalStateKey(params.clientId, params.serviceRequestId, params.formCode);
    void this.loadFormStateRecord(params.clientId, params.serviceRequestId, params.formCode).then((state) => {
      if (isPendingLocalComplete(state)) {
        return;
      }
      this.enqueueLocalCommit(stateKey, params, onCommitted);
    });
  }

  private enqueueLocalCommit(
    stateKey: string,
    params: CommitLocalSaveParams,
    onCommitted?: () => void,
  ): void {
    const existing = this.localCommitTimers.get(stateKey);
    if (existing) {
      clearTimeout(existing.timer);
    }

    const timer = setTimeout(() => {
      this.localCommitTimers.delete(stateKey);
      void this.runScheduledLocalCommit(params, onCommitted);
    }, 500);

    this.localCommitTimers.set(stateKey, { timer, params, onCommitted });
  }

  async flushPendingLocalCommit(stateKey: string): Promise<void> {
    const pending = this.localCommitTimers.get(stateKey);
    if (!pending) {
      return;
    }

    clearTimeout(pending.timer);
    this.localCommitTimers.delete(stateKey);
    await this.runScheduledLocalCommit(pending.params, pending.onCommitted);
  }

  private async runScheduledLocalCommit(
    params: CommitLocalSaveParams,
    onCommitted?: () => void,
  ): Promise<void> {
    if (!params.formPlacementId) {
      return;
    }

    try {
      await this.commitLocalSave(params);
      onCommitted?.();
    } catch {
      // commitLocalSave surfaces quota errors via toast
    }
  }

  async commitLocalSave(params: CommitLocalSaveParams): Promise<OfflineFormLocalState> {
    const {
      clientId,
      serviceRequestId,
      formCode,
      formId,
      formPlacementId,
      mode,
      formSnapshot,
      capturedAt,
      pendingComplete: pendingCompleteParam,
    } = params;

    const bundle = await this.storage.findBundleByFormCode(
      clientId,
      serviceRequestId,
      formCode,
      formPlacementId,
    );
    const technicianId = bundle?.technicianId;
    if (!technicianId) {
      throw new Error('Offline technician context is missing for this form.');
    }

    const stateKey = buildFormLocalStateKey(clientId, serviceRequestId, formCode);
    const existing = await this.storage.getFormLocalState(stateKey);
    const clientMutationId = existing?.clientMutationId ?? generateClientMutationId();
    const capturedAtIso = capturedAt ?? new Date().toISOString();

    const snapshot = structuredClone(formSnapshot);
    if (mode === 'create' && !('responseId' in snapshot)) {
      (snapshot as FormResponseDetails).responseId = existing?.responseId ?? generateTempResponseId();
    }

    const pendingComplete = pendingCompleteParam === true;
    const clearedPendingComplete = pendingCompleteParam === false;

    const state: OfflineFormLocalState = {
      stateKey,
      clientId,
      serviceRequestId,
      formCode: normalizeFormCode(formCode),
      formId,
      formPlacementId,
      technicianId,
      mode,
      responseId: 'responseId' in snapshot ? snapshot.responseId : existing?.responseId,
      clientMutationId,
      formSnapshot: snapshot,
      capturedAt: capturedAtIso,
      lastModifiedAt: capturedAtIso,
      syncStatus: 'saved-locally',
      pendingMutationId: undefined,
      pendingComplete: pendingComplete
        ? true
        : clearedPendingComplete
          ? undefined
          : existing?.pendingComplete,
      lastSyncedAt: existing?.lastSyncedAt,
    };

    await this.persistFormLocalState(state);
    const optimisticResponseJson = buildOptimisticOfflineResponseJson(snapshot, mode);
    await this.writeOptimisticOfflineContextCache(
      clientId,
      serviceRequestId,
      formPlacementId,
      optimisticResponseJson,
      pendingComplete ? SERVICE_REQUEST_FORM_SUBMISSION_STATUS.COMPLETED : undefined,
    );
    this.ui.setFormSyncStatus('saved-locally');
    return state;
  }

  async resolveAfterOnlineSubmit(
    clientId: string,
    serviceRequestId: number,
    formCode: string,
  ): Promise<void> {
    const state = await this.loadFormStateRecord(clientId, serviceRequestId, formCode);
    if (!state) {
      return;
    }

    if (state.pendingMutationId) {
      await this.queue.remove(state.pendingMutationId);
    }

    await this.markSynced(clientId, serviceRequestId, formCode);
  }

  async markSyncing(clientId: string, serviceRequestId: number, formCode: string): Promise<void> {
    await this.updateSyncStatus(clientId, serviceRequestId, formCode, 'syncing');
  }

  async markSyncFailed(clientId: string, serviceRequestId: number, formCode: string): Promise<void> {
    await this.updateSyncStatus(clientId, serviceRequestId, formCode, 'sync-failed');
  }

  async markSynced(clientId: string, serviceRequestId: number, formCode: string): Promise<void> {
    await this.updateSyncStatus(clientId, serviceRequestId, formCode, 'synced', new Date().toISOString());
  }

  async resolveAfterSync(
    clientId: string,
    serviceRequestId: number,
    formCode: string,
    formId: number,
    serverResponse: FormResponseDetails,
  ): Promise<void> {
    const stateKey = buildFormLocalStateKey(clientId, serviceRequestId, formCode);
    const existing = await this.storage.getFormLocalState(stateKey);
    const now = new Date().toISOString();

    const state: OfflineFormLocalState = {
      stateKey,
      clientId,
      serviceRequestId,
      formCode: normalizeFormCode(formCode),
      formId,
      mode: 'edit',
      responseId: serverResponse.responseId,
      clientMutationId: existing?.clientMutationId ?? generateClientMutationId(),
      formSnapshot: structuredClone(serverResponse),
      lastModifiedAt: now,
      syncStatus: 'synced',
      lastSyncedAt: now,
    };

    await this.persistFormLocalState(state);
    const placementId = existing?.formPlacementId;
    if (placementId) {
      const optimisticResponseJson = buildOptimisticOfflineResponseJson(serverResponse, 'edit');
      await this.writeOptimisticOfflineContextCache(
        clientId,
        serviceRequestId,
        placementId,
        optimisticResponseJson,
      );
    }

    this.ui.setFormSyncStatus(null);
  }

  private async loadFormStateRecord(
    clientId: string,
    serviceRequestId: number,
    formCode: string,
  ): Promise<OfflineFormLocalState | undefined> {
    const direct = await this.storage.getFormLocalState(
      buildFormLocalStateKey(clientId, serviceRequestId, formCode),
    );
    if (direct) {
      return direct;
    }

    const normalized = normalizeFormCode(formCode);
    const states = await this.storage.listFormLocalStates();
    return states.find((state) =>
      state.clientId === clientId
      && state.serviceRequestId === serviceRequestId
      && (
        normalizeFormCode(state.formCode ?? state.formSnapshot.formCode ?? '') === normalized
        || normalizeFormCode(state.formSnapshot.formCode ?? '') === normalized
      ),
    );
  }

  private async updateSyncStatus(
    clientId: string,
    serviceRequestId: number,
    formCode: string,
    syncStatus: OfflineSyncStatus,
    lastSyncedAt?: string,
  ): Promise<void> {
    const state = await this.loadFormStateRecord(clientId, serviceRequestId, formCode);
    if (!state) {
      return;
    }

    await this.persistFormLocalState({
      ...state,
      syncStatus,
      lastSyncedAt: lastSyncedAt ?? state.lastSyncedAt,
      pendingMutationId: syncStatus === 'synced' ? undefined : state.pendingMutationId,
      pendingComplete: syncStatus === 'synced' ? undefined : state.pendingComplete,
    });
    this.ui.setFormSyncStatus(syncStatus === 'synced' ? null : syncStatus);
  }

  private async persistFormLocalState(state: OfflineFormLocalState): Promise<void> {
    try {
      await this.storage.putFormLocalState(state);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        this.toastService.show(
          $localize`:@@offlineStorageQuotaExceeded:Offline storage is full. Sync your forms or free browser storage.`,
          TOAST_TYPE.ERROR,
        );
      }
      throw error;
    }
  }

  private async writeOptimisticOfflineContextCache(
    clientId: string,
    serviceRequestId: number,
    formPlacementId: number,
    payloadJson: string,
    submissionStatus?: string,
  ): Promise<void> {
    const now = new Date().toISOString();
    const offlineContextParams = { clientId, serviceRequestId, formPlacementId };
    const offlineContextKey = buildOfflineCacheKey(
      clientId,
      'SERVICE_REQUEST_FORM_OFFLINE_CONTEXT',
      offlineContextParams,
    );
    const existing = await this.storage.getHttpCache(offlineContextKey);
    const context = (existing?.body ?? {}) as OfflineFormContextResponse;

    await this.storage.putHttpCache({
      cacheKey: offlineContextKey,
      endpointKey: 'SERVICE_REQUEST_FORM_OFFLINE_CONTEXT',
      url: '',
      method: 'GET',
      body: {
        ...context,
        formPlacementId,
        serviceRequestId,
        responseJson: payloadJson,
        ...(submissionStatus ? { submissionStatus } : {}),
      },
      status: 200,
      cachedAt: now,
    });
  }

  private async resolvePlacementFromBundles(
    clientId: string,
    serviceRequestId: number,
    formId: number,
  ): Promise<ServiceRequestFormPlacementDetail | undefined> {
    const bundles = await this.storage.listBundles();
    const bundle = bundles.find((candidate) =>
      candidate.clientId === clientId
      && candidate.serviceRequestId === serviceRequestId
      && candidate.formId === formId,
    );
    if (!bundle) {
      return undefined;
    }

    const offlineContextKey = buildOfflineCacheKey(clientId, 'SERVICE_REQUEST_FORM_OFFLINE_CONTEXT', {
      clientId,
      serviceRequestId,
      formPlacementId: bundle.formsId,
    });
    const directOfflineContext = await this.storage.getHttpCache(offlineContextKey);
    if (directOfflineContext?.body) {
      const context = directOfflineContext.body as OfflineFormContextResponse;
      return mapOfflineContextToPlacementDetail(context, bundle.formCode ?? '');
    }

    for (const cacheKey of bundle.cacheKeyRefs) {
      const entry = await this.storage.getHttpCache(cacheKey);
      if (entry?.endpointKey === 'SERVICE_REQUEST_FORM_OFFLINE_CONTEXT' && entry.body) {
        const context = entry.body as OfflineFormContextResponse;
        return mapOfflineContextToPlacementDetail(context, bundle.formCode ?? '');
      }
    }

    for (const cacheKey of bundle.cacheKeyRefs) {
      const entry = await this.storage.getHttpCache(cacheKey);
      if (entry?.endpointKey === 'SERVICE_REQUEST_FORM_PLACEMENT' && entry.body) {
        return entry.body as ServiceRequestFormPlacementDetail;
      }
    }

    const placementKey = buildOfflineCacheKey(clientId, 'SERVICE_REQUEST_FORM_PLACEMENT', {
      clientId,
      serviceRequestId,
      placementId: bundle.formsId,
    });
    const directCache = await this.storage.getHttpCache(placementKey);
    return directCache?.body as ServiceRequestFormPlacementDetail | undefined;
  }
}
